<template>
  <section>
    <div
      v-show="
        showCategory('regions') ||
        showCategory('varietals') ||
        showCategory('wineries') ||
        subject.description
      "
      class="learn-collapsible"
      :class="expanded && 'expanded'"
      :aria-expanded="expanded"
    >
      <div class="collapsible-toggle-position">
        <button class="collapsible-toggle" @click="toggle()">
          Expand/Contract
        </button>
      </div>
      <div class="learn-main-content">
        <div v-if="header" class="learn-content-header" @click="toggle()">
          <h1>{{ header }}</h1>
        </div>
        <div class="learn-collapsible-content">
          <div class="learn-left-nav">
            <div
              v-for="(link, id) of navLinks"
              :key="id"
              class="learn-left-nav-section"
            >
              <h2>
                <a
                  class="learn-left-nav-link"
                  :href="`#${link.id}`"
                  :data-link-id="link.id"
                  data-type="learn-left-nav-link"
                  @click.prevent="scrollTo(link.id)"
                  >{{ link.text }}</a
                >
              </h2>
            </div>
          </div>
          <div class="learn-detail-content">
            <div
              v-if="subject.image && subject.flavor_profile"
              class="row text-center square-container"
            >
              <div class="columns small-6 image-cropper">
                <img
                  :alt="subject.name"
                  class="varietal-pic"
                  :src="subject.image"
                />
              </div>
              <div class="columns small-6 text-center">
                <h2>Varietal Facts</h2>
                <p>{{ subject.flavor_profile }}</p>
              </div>
            </div>
            <div
              v-else-if="subject.image && subject.flavor_profile === undefined"
              class="row text-center square-container"
            >
              <div class="columns small-9 image-cropper category-image">
                <img
                  :alt="subject.name"
                  class="varietal-pic"
                  :src="subject.image"
                />
              </div>
            </div>

            <LearnCategoryRegions v-if="showCategory('regions')" />
            <LearnCategoryVarietals v-else-if="showCategory('varietals')" />
            <LearnCategoryWineries v-else-if="showCategory('wineries')" />
            <div v-else id="learn_content" v-html="description"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { scrollToElement } from '#common/utils/form'
import LearnCategoryRegions from './LearnCategoryRegions.vue'
import LearnCategoryVarietals from './LearnCategoryVarietals.vue'
import LearnCategoryWineries from './LearnCategoryWineries.vue'

const LEARN_INDEXES = {
  wineries: {
    name: 'Wineries',
    image: import('~assets/images/learn/winery_section_header.png'),
  },
  varietals: {
    name: 'Grape Varietals',
    image: import('~assets/images/learn/varietal_section_header.png'),
  },
  regions: {
    name: 'Wine Growing Regions',
    image: import('~assets/images/learn/region_section_header.png'),
  },
}

export default {
  components: {
    LearnCategoryRegions,
    LearnCategoryVarietals,
    LearnCategoryWineries,
  },
  props: {
    startExpanded: Boolean,
  },
  async setup() {
    const { $axios } = useNuxtApp()
    const metadata = useMetadata()
    const route = useRoute()
    const { category, slug } = route.params

    const subject = ref({})

    if (['regions', 'varietals', 'wineries'].includes(category)) {
      if (slug) {
        subject.value = (
          await $axios.get(`/api/learn/${category}/${slug}`).catch(() => {
            return { data: {} } // Ignore failed requests
          })
        ).data
      } else {
        subject.value = LEARN_INDEXES[category]
      }
    } else if (
      route.name === 'wine-club' &&
      metadata.constance.WINE_CLUB_LEARN
    ) {
      subject.value = {
        name: 'Wine Clubs',
        description: metadata.constance.WINE_CLUB_LEARN,
      }
    }

    return {
      expanded: ref(false),
      subject,
      title: ref(''),
      description: ref(''),
      navLinks: ref([]), // { id: string, text: string }[]
    }
  },
  computed: {
    header() {
      if (!this.subject.name) {
        return ''
      }
      const slug = this.$route.params.slug
      // Get last section if name has breadcrumbs
      const name = this.subject.name.split(' > ').pop()
      if (slug) {
        return `About ${name}`
      } else {
        return name
      }
    },
  },
  watch: {
    subject() {
      this.resetContent()
    },
    startExpanded() {
      this.resetContent()
    },
  },
  mounted() {
    this.resetContent()
  },
  methods: {
    toggle() {
      this.expanded = !this.expanded
    },
    scrollTo(id) {
      scrollToElement(this.$nextTick, `#learn_content #${id}`)
    },
    resetContent() {
      // If there was an error fetching the content, ignore it
      if (!this.subject.description) {
        return
      }

      // Set initial expanded state
      this.expanded = Boolean(this.startExpanded)

      // Convert description to DOM node
      const root = document.createElement('template')
      root.innerHTML = this.subject.description

      // Remove header
      root.content
        .querySelectorAll('h1')
        .forEach((h1) => root.content.removeChild(h1))

      // Generate nav links
      this.navLinks = []
      const ids = []
      for (const sectionHeader of root.content.querySelectorAll('h2,h3')) {
        // Set ID from text content, if necessary
        if (!sectionHeader.id) {
          sectionHeader.id = sectionHeader.textContent
        }
        // Normalize and clean up IDs
        sectionHeader.id = sectionHeader.id
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '_')
        // Check for duplicates
        while (ids.includes(sectionHeader.id)) {
          sectionHeader.id += '_'
        }
        ids.push(sectionHeader.id)
        // Add to learn content nav links
        this.navLinks.push({
          id: sectionHeader.id,
          text: sectionHeader.textContent,
        })
      }

      // Set description
      this.description = root.innerHTML
    },
    showCategory(category) {
      return (
        this.$route.params.category === category &&
        this.$route.params.slug === undefined
      )
    },
  },
}
</script>
